import api from '../../api';
import apiHris from '../../apiHris';

const BASE = '/hris';
const HRIS_GO_BASE = '/v1/hris';
const STORAGE_PREFIX = 'onealpha_hris_';

const safeText = (value) => String(value ?? '').toLowerCase();

const normalizePage = (records, params = {}) => {
  const page = Number(params.page ?? 0);
  const size = Number(params.size ?? 25);
  const search = safeText(params.search).trim();
  const filtered = search
    ? records.filter((record) => Object.values(record).some((value) => safeText(value).includes(search)))
    : records;
  const start = page * size;
  const content = filtered.slice(start, start + size);
  return {
    content,
    totalElements: filtered.length,
    totalPages: Math.max(1, Math.ceil(filtered.length / size)),
    number: page,
    size,
    _source: 'local-draft',
  };
};

const readLocal = (resource) => {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${resource}`);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
};

const writeLocal = (resource, records) => {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${resource}`, JSON.stringify(records));
  } catch (_) {}
  return records;
};

const localId = (resource) => `${resource}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const localList = (resource, params) => normalizePage(readLocal(resource), params);

const localCreate = (resource, data) => {
  const records = readLocal(resource);
  const now = new Date().toISOString();
  const record = {
    id: localId(resource),
    ...data,
    createdAt: now,
    updatedAt: now,
    _source: 'local-draft',
  };
  writeLocal(resource, [record, ...records]);
  return record;
};

const localUpdate = (resource, id, data) => {
  const records = readLocal(resource);
  const now = new Date().toISOString();
  let updated = null;
  const next = records.map((record) => {
    if (String(record.id) !== String(id)) return record;
    updated = { ...record, ...data, id: record.id, updatedAt: now, _source: record._source || 'local-draft' };
    return updated;
  });
  writeLocal(resource, next);
  return updated || { id, ...data, updatedAt: now, _source: 'local-draft' };
};

const localDelete = (resource, id) => {
  const records = readLocal(resource).filter((record) => String(record.id) !== String(id));
  writeLocal(resource, records);
  return { success: true, _source: 'local-draft' };
};

const query = (params = {}) => {
  const p = new URLSearchParams({ page: params.page ?? 0, size: params.size ?? 25 });
  Object.entries(params).forEach(([key, value]) => {
    if (!['page', 'size'].includes(key) && value !== undefined && value !== null && value !== '') {
      p.append(key, value);
    }
  });
  return p.toString();
};

const list = (resource) => (params = {}) => (
  api.get(`${BASE}/${resource}?${query(params)}`)
    .then((r) => r.data)
    .catch(() => localList(resource, params))
);

const create = (resource) => (data) =>
  api.post(`${BASE}/${resource}`, data).then((r) => r.data);

const update = (resource) => (id, data) =>
  api.put(`${BASE}/${resource}/${id}`, data).then((r) => r.data);

const remove = (resource) => (id) =>
  api.delete(`${BASE}/${resource}/${id}`).then((r) => r.data);

// Dashboard is still a CRM master served by the Java backend's
// HrisCrmMasterController. Employees and Departments do NOT use this base —
// both are served live from Datahub via the Go PM backend's HRIS module
// (lib/mdclient), which has no local table to be a "CRM master" over.
const CRM_HRIS_BASE = '/crm/hris';

export const getEmployees = (params = {}) =>
  apiHris.get(`${HRIS_GO_BASE}/employees?${query(params)}`).then((r) => r.data);
export const getEmployeeById = (id) =>
  apiHris.get(`${HRIS_GO_BASE}/employees/${id}`).then((r) => r.data);
export const getEmployeeDocuments = (id) =>
  apiHris.get(`${HRIS_GO_BASE}/employees/${id}/documents`).then((r) => r.data);
export const getEmployeeCompetencies = (id) =>
  api.get(`/audit/competencies/requests/user/${id}`).then((r) => r.data);

// Company-wide competency roster (who has what competency) — every status
// (pending/approved/rejected/revision) included, HRIS Dashboard filters client-side.
export const getAllCompetencies = () =>
  api.get('/audit/competencies/requests').then((r) => r.data);
export const createEmployee = (data) =>
  apiHris.post(`${HRIS_GO_BASE}/employees`, data).then((r) => r.data);
export const updateEmployee = (id, data) =>
  apiHris.put(`${HRIS_GO_BASE}/employees/${id}`, data).then((r) => r.data);
export const deleteEmployee = (id) =>
  apiHris.delete(`${HRIS_GO_BASE}/employees/${id}`).then((r) => r.data);
export const getEmployeeFilterOptions = () =>
  apiHris.get(`${HRIS_GO_BASE}/employees/filter-options`).then((r) => r.data);

export const getEmployeeList = () =>
  apiHris.get(`${HRIS_GO_BASE}/employees/list`).then((r) => r.data);

export const getLeaves = list('leaves');
export const createLeave = create('leaves');
export const updateLeave = update('leaves');
export const deleteLeave = remove('leaves');
export const approveLeave = (id) => api.post(`${BASE}/leaves/${id}/approve`).then((r) => r.data);
export const rejectLeave = (id, reason) => api.post(`${BASE}/leaves/${id}/reject`, { reason }).then((r) => r.data);

export const getAttendance = list('attendance');
export const createAttendance = create('attendance');
export const updateAttendance = update('attendance');
export const deleteAttendance = remove('attendance');

export const getPayroll = list('payroll');
export const createPayroll = create('payroll');
export const updatePayroll = update('payroll');
export const deletePayroll = remove('payroll');

export const getRecruitment = list('recruitment');
export const createJobOpening = create('recruitment');
export const updateJobOpening = update('recruitment');
export const deleteJobOpening = remove('recruitment');

export const getPerformance = list('performance');
export const createAppraisal = create('performance');
export const updateAppraisal = update('performance');
export const deleteAppraisal = remove('performance');

// Special handling for expenses to support dummy data fallback
export const getHRExpenses = (params = {}) => (
  api.get(`${BASE}/expenses?${query(params)}`)
    .then((r) => {
      // If backend returns empty but localStorage has data, use localStorage
      const result = r.data;
      if ((!result.content || result.content.length === 0) && readLocal('expenses').length > 0) {
        return localList('expenses', params);
      }
      return result;
    })
    .catch(() => localList('expenses', params))
);

export const createHRExpense = create('expenses');
export const updateHRExpense = update('expenses');
export const deleteHRExpense = remove('expenses');

export const getExpenseClaimTypes = (params = {}) => (
  api.get(`${BASE}/expense-claim-types?${query(params)}`)
    .then((r) => {
      const result = r.data;

      if (
        (!result?.content || result.content.length === 0) &&
        readLocal('expense-claim-types').length > 0
      ) {
        return localList(
          'expense-claim-types',
          params
        );
      }

      return result;
    })
    .catch(() =>
      localList(
        'expense-claim-types',
        params
      )
    )
);

// Special handling for employee advances to support dummy data fallback
export const getEmployeeAdvances = (params = {}) => (
  api.get(`${BASE}/employee-advances?${query(params)}`)
    .then((r) => {
      const result = r.data;

      if (
        (!result?.content || result.content.length === 0) &&
        readLocal('employee-advances').length > 0
      ) {
        return localList('employee-advances', params);
      }

      return result;
    })
    .catch(() =>
      localList('employee-advances', params)
    )
);

export const createEmployeeAdvance = create('employee-advances');
export const updateEmployeeAdvance = update('employee-advances');
export const deleteEmployeeAdvance = remove('employee-advances');

export const createExpenseClaimType = create('expense-claim-types');
export const updateExpenseClaimType = update('expense-claim-types');
export const deleteExpenseClaimType = remove('expense-claim-types');

// Departments are sourced live (read-only) from Datahub via the Go HRIS
// module — Create/Update/Delete are still wired to real requests (mirroring
// getEmployees'/createEmployee's convention) so the backend's own explicit
// "not supported, manage in Datahub" error surfaces to the user, rather
// than a client-side-only rejection with a possibly-different message.
export const getDepartments = (params = {}) =>
  apiHris.get(`${HRIS_GO_BASE}/departments?${query(params)}`).then((r) => r.data);
export const createDepartment = (data) =>
  apiHris.post(`${HRIS_GO_BASE}/departments`, data).then((r) => r.data);
export const updateDepartment = (id, data) =>
  apiHris.put(`${HRIS_GO_BASE}/departments/${id}`, data).then((r) => r.data);
export const deleteDepartment = (id) =>
  apiHris.delete(`${HRIS_GO_BASE}/departments/${id}`).then((r) => r.data);
export const getHrisDashboard = () => api.get(`${CRM_HRIS_BASE}/dashboard`).then((r) => r.data);
export const exportDepartmentExcel = async (params) => {
  // backend call to export department data to Excel
};

export const exportDepartmentPDF = async (params) => {
  // backend call to export department data to PDF
};


// Job titles are sourced live (read-only) from Datahub via the Go HRIS
// module, same convention as Employees/Departments — Create/Update/Delete
// still issue real requests so the backend's explicit "not supported,
// manage in Datahub" error surfaces to the user.
export const getDesignations = (params = {}) =>
  apiHris.get(`${HRIS_GO_BASE}/job-titles?${query(params)}`).then((r) => r.data);
export const createDesignation = (data) =>
  apiHris.post(`${HRIS_GO_BASE}/job-titles`, data).then((r) => r.data);
export const updateDesignation = (id, data) =>
  apiHris.put(`${HRIS_GO_BASE}/job-titles/${id}`, data).then((r) => r.data);
export const deleteDesignation = (id) =>
  apiHris.delete(`${HRIS_GO_BASE}/job-titles/${id}`).then((r) => r.data);

export const getBranches = list('branches');
export const createBranch = create('branches');
export const updateBranch = update('branches');
export const deleteBranch = remove('branches');

export const getTaxBenefits = list('tax-benefits');
export const createTaxBenefit = create('tax-benefits');
export const updateTaxBenefit = update('tax-benefits');
export const deleteTaxBenefit = remove('tax-benefits');

export const getTenure = list('tenure');
export const createTenureRecord = create('tenure');
export const updateTenureRecord = update('tenure');
export const deleteTenureRecord = remove('tenure');
